import React, { useState } from "react";
import {
  TriangleAlert,
  PlusCircle,
  Activity,
  ScrollText
} from 'lucide-react';

// --- Chart.js Imports ---
import {
  Chart as ChartJS,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { MatrixController, MatrixElement } from 'chartjs-chart-matrix';

import Card from "../components/card";
import CardHeader from "../components/cardHeader";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  MatrixController,
  MatrixElement
);

// --- Heatmap (Chart.js) ---
const Heatmap = () => {
  const rawData = [
    [5, 7, 6, 8, 10], // Very High
    [12, 8, 4, 6, 8], // High
    [4, 3, 8, 3, 5],  // Medium
    [1, 2, 3, 3, 4],  // Low
    [3, 1, 1, 1, 2],  // Very Low
  ];

  const yLabels = ["Very High", "High", "Medium", "Low", "Very Low"];
  const xLabels = ["Critical", "High", "Medium", "Low", "Minimal"];

  // Convert nested array into {x, y, v} objects required by the matrix plugin
  const matrixData = [];
  rawData.forEach((row, rIdx) => {
    row.forEach((val, cIdx) => {
      matrixData.push({
        x: xLabels[cIdx],
        y: yLabels[rIdx],
        v: val,
      });
    });
  });

  const data = {
    datasets: [
      {
        label: "Risk Distribution",
        data: matrixData,
        backgroundColor: (context) => {
          const val = context.raw?.v;
          if (val === undefined) return "transparent";
          if (val >= 8) return "#f43f5e"; // rose-500
          if (val >= 5) return "#f97316"; // orange-500
          if (val >= 3) return "#fbbf24"; // amber-400
          if (val > 0) return "#34d399";  // emerald-400
          return "#f8fafc";               // slate-50 (Empty)
        },
        borderRadius: 8,
        borderWidth: 0,
        hoverBackgroundColor: "#6366f1", 
        // Dynamically calculate block sizes with padding, ensuring no crashes
        width: ({ chart }) => (chart.chartArea ? (chart.chartArea.width / 5) - 6 : 0),
        height: ({ chart }) => (chart.chartArea ? (chart.chartArea.height / 5) - 6 : 0),
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 10,
        bottom: 10,
        left: 10,
        right: 10
      }
    },
    animation: {
      duration: 600,
      easing: 'easeOutQuart'
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "rgba(15, 23, 42, 0.95)",
        padding: 16,
        cornerRadius: 12,
        titleFont: { size: 13, weight: "bold", family: "Inter, sans-serif" },
        bodyFont: { size: 13, family: "Inter, sans-serif" },
        titleColor: "#94a3b8", 
        bodyColor: "#f8fafc",  
        displayColors: false,
        callbacks: {
          title: (context) => `IMPACT: ${context[0].raw.x.toUpperCase()}`,
          label: (context) => `Likelihood: ${context.raw.y}  •  Count: ${context.raw.v}`,
        },
      },
    },
    scales: {
      x: {
        type: "category",
        labels: xLabels,
        offset: true, // CRITICAL: Centers the blocks between grid lines so labels align perfectly
        grid: { display: false, drawBorder: false },
        title: { 
          display: true, 
          text: "Impact", 
          font: { weight: "900", size: 11,  letterSpacing: 2 },
          color: "#94a3b8",
          padding: { top: 12 }
        },
        ticks: { 
          color: "#64748b", 
          font: { weight: "600", size: 12,  }
        }
      },
      y: {
        type: "category",
        labels: yLabels,
        reverse: true, 
        offset: true, // CRITICAL: Prevents top/bottom rows from getting cut off
        grid: { display: false, drawBorder: false },
        title: { 
          display: true, 
          text: "Likelihood", 
          font: { weight: "900", size: 11,  letterSpacing: 2 },
          color: "#94a3b8",
          padding: { bottom: 12 }
        },
        ticks: { 
          color: "#64748b", 
          font: { weight: "600", size: 12,  }
        }
      },
    },
  };

  // Custom plugin to draw numbers cleanly inside the matrix blocks
const dataLabelsPlugin = {
    id: "matrixDataLabels",
    afterDatasetsDraw: (chart) => {
      const { ctx } = chart;
      
      chart.data.datasets.forEach((dataset, i) => {
        const meta = chart.getDatasetMeta(i);
        
        meta.data.forEach((element, index) => {
          const val = dataset.data[index].v;
          
          // 1. Force Chart.js to give us the final animated properties
          const props = element.getProps(['x', 'y', 'width', 'height'], true);
          
          if (props.x === undefined || props.y === undefined) return;

          // 2. chartjs-chart-matrix sometimes uses x/y as center, sometimes top-left.
          // Using tooltipPosition() guarantees we get the exact geometric center.
          const center = element.tooltipPosition();

          ctx.save();
          
          // 3. Styling
          ctx.fillStyle = val >= 5 ? "#ffffff" : val > 0 ? "#1e293b" : "#ffffff";
          ctx.font = "bold 12px 'Inter', '";
          
          // 4. Alignment
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          
          // 5. Draw text at the exact tooltip center coordinates
          // If the font natively sits a bit high/low, you can manually nudge it by adding "+ 1" to center.y
          ctx.fillText(val === 0 ? "·" : val, center.x, center.y);
          
          ctx.restore();
        });
      });
    },
  };

  return (
    <Card className="p-7 h-full flex flex-col border border-slate-200 shadow-sm rounded-3xl">
      <CardHeader 
        title="Risk Heatmap" 
        subtitle="Visual distribution of likelihood vs. impact" 
        icon={Activity} 
        colorClass="bg-indigo-50 text-indigo-600" 
      />
      
      {/* Legend */}
      <div className="flex items-center justify-end gap-3 mt-2 mb-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500"><span className="w-2.5 h-2.5 rounded-sm bg-rose-500"></span>Critical</div>
        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500"><span className="w-2.5 h-2.5 rounded-sm bg-orange-500"></span>High</div>
        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500"><span className="w-2.5 h-2.5 rounded-sm bg-amber-400"></span>Medium</div>
        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-400"></span>Low</div>
      </div>

      <div className="flex-1 w-full min-h-[350px] relative">
        <Chart 
            type="matrix" 
            data={data} 
            options={options} 
            plugins={[dataLabelsPlugin]} 
        />
      </div>
    </Card>
  );
};

// --- Top Risks ---
const TopRisks = () => (
  <Card className="p-7 h-full">
    <CardHeader 
      title="Top Risks List" 
      subtitle="Highest priority items requiring immediate attention" 
      icon={ScrollText} 
      colorClass="bg-indigo-50 text-indigo-600" 
    />
    <div className="mt-4 flex flex-col gap-3">
      {["High", "High", "Medium", "Low"].map((level, i) => (
        <div key={i} className="flex justify-between items-center border border-slate-200 rounded-xl px-4 py-3 bg-slate-50/50 hover:bg-slate-50 transition-colors">
          <div>
            <p className="text-sm font-semibold text-slate-800">AI Evaluated Finding</p>
            <p className="text-xs text-slate-500 mt-0.5 font-medium">Linked Control: ITGC-AC-01</p>
          </div>
          <span className={`text-xs px-2.5 py-1 rounded-md font-semibold
            ${level === 'High' ? 'bg-red-100 text-red-700' : 
              level === 'Medium' ? 'bg-orange-100 text-orange-700' : 
              'bg-green-100 text-green-700'}`}>
            {level}
          </span>
        </div>
      ))}
    </div>
  </Card>
);

// --- Risk Card ---
const RiskCard = () => (
  <div className="border border-slate-200 rounded-2xl p-5 bg-white shadow-sm hover:shadow-md transition-shadow">
    <div className="flex justify-between items-start mb-3">
      <h4 className="font-bold text-slate-800">AI Evaluated Finding</h4>
      <span className="text-xs font-bold bg-yellow-100 text-yellow-800 px-2.5 py-1 rounded-md">OPEN</span>
    </div>

    <p className="text-sm text-slate-600 leading-relaxed mb-4">
      The evidence primarily consists of an RFP outlining requirements and intentions.
    </p>

    <div className="flex items-center gap-2 text-xs font-medium text-slate-500 pt-3 border-t border-slate-100">
      <span className="bg-slate-100 px-2 py-1 rounded">Severity: Medium</span>
      <span className="bg-slate-100 px-2 py-1 rounded">Assign To: Pending</span>
      <span className="bg-slate-100 px-2 py-1 rounded">Due: N/A</span>
    </div>
  </div>
);

// --- Remediation Card ---
const RemediationCard = () => (
  <div className="border border-slate-200 rounded-2xl p-5 bg-white shadow-sm hover:shadow-md transition-shadow">
    <div className="flex justify-between items-start mb-3">
      <h4 className="font-bold text-slate-800">Implement RFP Review for AI</h4>
      <span className="text-xs font-bold bg-indigo-100 text-indigo-700 px-2.5 py-1 rounded-md">In Progress</span>
    </div>

    <p className="text-sm text-slate-600 leading-relaxed mb-4">
      Addresses Risk: <span className="font-semibold text-slate-700">AI-GAP-1</span> (AI Evaluated Finding)
    </p>

    <div className="mt-auto pt-3 border-t border-slate-100">
      <div className="flex justify-between items-center mb-1.5">
        <span className="text-xs font-bold text-slate-700">Progress</span>
        <span className="text-xs font-bold text-indigo-600">60%</span>
      </div>
      <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-indigo-500 rounded-full w-[60%] transition-all duration-500" />
      </div>
    </div>
  </div>
);

// --- Main Page ---
export default function RisksPage() {
  const [tab, setTab] = useState("risks");

  return (
    <div className="animate-in fade-in zoom-in-[0.98] duration-500 ease-out mx-auto">
      <div className="bg-slate-50/50 relative">
        {/* --- HEADER --- */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-indigo-600 rounded-2xl shadow-lg shadow-indigo-600/20 text-white">
              <TriangleAlert size={28} strokeWidth={2} />
            </div>
            <div>
              <h3 className="text-2xl font-black text-slate-900 tracking-tight">Risks & Remediations Center</h3>
              <p className="text-sm font-medium text-slate-500 mt-1">Manage the key stakeholders assigned to this compliance project.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button className="flex gap-2 items-center px-4 py-2.5 bg-white border border-slate-200 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 hover:text-slate-900 transition-all shadow-sm active:scale-95 text-sm">
              <PlusCircle size={18} />
              Raise a Risk
            </button>
            <button className="flex gap-2 items-center px-4 py-2.5 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-all shadow-md active:scale-95 text-sm">
              <PlusCircle size={18} />
              Create Remediation Plan
            </button>
          </div>
        </div>

        {/* --- CONTENT --- */}
        <div className="relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Heatmap />
            <TopRisks />
          </div>

          <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-200 mt-8">
            
            {/* Updated Tabs UI/UX */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <div className="inline-flex bg-slate-100/80 p-1.5 rounded-xl">
                <button
                  onClick={() => setTab("risks")}
                  className={`px-5 py-2 rounded-lg text-sm font-bold transition-all duration-200 ease-in-out ${
                    tab === "risks" 
                      ? "bg-white text-indigo-700 shadow-sm ring-1 ring-slate-900/5" 
                      : "text-slate-500 hover:text-slate-800 hover:bg-slate-200/50"
                  }`}
                >
                  Risks List
                </button>
                <button
                  onClick={() => setTab("remediations")}
                  className={`px-5 py-2 rounded-lg text-sm font-bold transition-all duration-200 ease-in-out ${
                    tab === "remediations" 
                      ? "bg-white text-indigo-700 shadow-sm ring-1 ring-slate-900/5" 
                      : "text-slate-500 hover:text-slate-800 hover:bg-slate-200/50"
                  }`}
                >
                  Remediations List
                </button>
              </div>

              {/* Filters */}
              <div className="flex flex-wrap items-center gap-2">
                {["Severity", "Status", "Control Framework", "Owner"].map((f) => (
                  <button key={f} className="border border-slate-200 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 bg-white hover:bg-slate-50 transition-colors">
                    {f}
                  </button>
                ))}
                <input
                  placeholder="Search items..."
                  className="ml-auto border border-slate-200 px-4 py-1.5 rounded-lg text-sm font-medium placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                />
              </div>
            </div>

            {/* Content Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {tab === "risks"
                ? Array.from({ length: 6 }).map((_, i) => <RiskCard key={i} />)
                : Array.from({ length: 6 }).map((_, i) => <RemediationCard key={i} />)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}